import type { ReactElement } from 'react';
import type {
  WorkspaceBillingActivityContract,
  WorkspaceBillingActivityPageContract,
  WorkspaceBillingActivityType,
} from '@nest-react/domain';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui';
import { CreditChangeLabel } from '~/components/layout/CreditLabel';

const types: WorkspaceBillingActivityType[] = [
  'GENERATION',
  'TOP_UP',
  'PLAN_GRANT',
  'CARRYOVER',
  'PROMOTION',
  'REFUND',
  'ADMIN_ADJUSTMENT',
  'EXPIRY',
  'OTHER',
];

export const CreditUsageTable = ({
  page,
  activityType,
  loading,
  error,
  onTypeChange,
  onCursorChange,
}: {
  page: WorkspaceBillingActivityPageContract | null;
  activityType: WorkspaceBillingActivityType | null;
  loading: boolean;
  error: string;
  onTypeChange: (type: WorkspaceBillingActivityType | null) => void;
  onCursorChange: (cursor: string | null) => void;
}): ReactElement => {
  const { t, i18n } = useTranslation('billing');
  const locale = i18n.resolvedLanguage;

  return (
    <section className="overflow-hidden rounded-3xl border border-border bg-elevated shadow-sm">
      <div className="flex flex-col gap-4 border-b border-border-subtle p-6 sm:flex-row sm:items-end sm:justify-between sm:p-8">
        <div className="min-w-0">
          <h2 className="text-balance text-xl font-semibold text-foreground">
            {t('creditUsage')}
          </h2>
          <p className="mt-1 text-sm text-muted">{t('creditUsageHint')}</p>
        </div>
        <label className="grid gap-1.5 text-sm font-medium text-foreground">
          {t('activityTypeFilter')}
          <select
            aria-label={t('activityTypeFilter')}
            className="min-h-11 rounded-xl border border-border bg-background px-3 text-foreground focus-visible:outline-2 focus-visible:outline-primary"
            onChange={event =>
              onTypeChange(
                (event.target.value ||
                  null) as WorkspaceBillingActivityType | null
              )
            }
            value={activityType ?? ''}
          >
            <option value="">{t('allActivityTypes')}</option>
            {types.map(type => (
              <option key={type} value={type}>
                {t(`activityTypes.${type}`)}
              </option>
            ))}
          </select>
        </label>
      </div>

      {error ? (
        <p
          aria-live="polite"
          className="m-6 break-words rounded-2xl bg-destructive/10 p-4 text-sm text-destructive sm:m-8"
        >
          {error}
        </p>
      ) : loading ? (
        <p aria-live="polite" className="p-8 text-sm text-muted">
          {t('activityLoading')}
        </p>
      ) : !page?.items.length ? (
        <p className="p-8 text-sm text-muted">{t('noActivity')}</p>
      ) : (
        <>
          <table
            aria-label={t('creditUsage')}
            className="hidden w-full border-collapse md:table"
          >
            <thead>
              <tr className="border-b border-border-subtle text-left text-xs font-semibold uppercase tracking-wide text-muted">
                {['date', 'event', 'type', 'change', 'status'].map(key => (
                  <th className="px-6 py-4 first:pl-8 last:pr-8" key={key}>
                    {t(`activityColumns.${key}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border-subtle">
              {page.items.map(item => (
                <tr key={item.id}>
                  <td className="whitespace-nowrap px-6 py-5 pl-8 text-sm text-muted">
                    {formatDate(item.occurredAt, locale)}
                  </td>
                  <td className="max-w-md break-words px-6 py-5 text-sm font-medium text-foreground">
                    {t(item.eventCode, item.eventParams)}
                  </td>
                  <td className="px-6 py-5 text-sm text-muted">
                    {t(`activityTypes.${item.type}`)}
                  </td>
                  <td className="px-6 py-5">
                    <CreditChangeLabel
                      amount={item.amount}
                      locale={locale ?? 'en'}
                    />
                  </td>
                  <td className="px-6 py-5 pr-8 text-sm text-muted">
                    {t(`activityStatuses.${item.status}`)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="divide-y divide-border-subtle md:hidden">
            {page.items.map(item => (
              <ActivityCard item={item} key={item.id} locale={locale} />
            ))}
          </div>
        </>
      )}

      <div className="flex justify-end gap-3 border-t border-border-subtle p-4 sm:px-8">
        <Button
          disabled={!page?.previousCursor || loading}
          onClick={() => onCursorChange(page?.previousCursor ?? null)}
          variant="secondary"
        >
          {t('previous')}
        </Button>
        <Button
          disabled={!page?.nextCursor || loading}
          onClick={() => onCursorChange(page?.nextCursor ?? null)}
          variant="secondary"
        >
          {t('next')}
        </Button>
      </div>
    </section>
  );
};

const ActivityCard = ({
  item,
  locale,
}: {
  item: WorkspaceBillingActivityContract;
  locale?: string;
}): ReactElement => {
  const { t } = useTranslation('billing');
  const resolvedLocale = locale ?? 'en';
  const rows: { label: string; value: ReactElement | string }[] = [
    { label: 'date', value: formatDate(item.occurredAt, locale) },
    { label: 'event', value: t(item.eventCode, item.eventParams) },
    { label: 'type', value: t(`activityTypes.${item.type}`) },
    {
      label: 'status',
      value: t(`activityStatuses.${item.status}`),
    },
  ];

  return (
    <dl className="grid gap-3 p-6">
      {rows.map(({ label, value }) => (
        <div
          className="flex min-w-0 items-start justify-between gap-5"
          key={label}
        >
          <dt className="text-xs font-semibold uppercase tracking-wide text-muted">
            {t(`activityColumns.${label}`)}
          </dt>
          <dd className="min-w-0 break-words text-right text-sm text-foreground">
            {value}
          </dd>
        </div>
      ))}
      <div className="flex min-w-0 items-start justify-between gap-5">
        <dt className="text-xs font-semibold uppercase tracking-wide text-muted">
          {t('activityColumns.change')}
        </dt>
        <dd className="min-w-0 text-right text-sm text-foreground">
          <CreditChangeLabel amount={item.amount} locale={resolvedLocale} />
        </dd>
      </div>
    </dl>
  );
};

const formatDate = (value: string, locale?: string): string =>
  new Intl.DateTimeFormat(locale, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
